import { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { TourProvider, useTour } from "@reactour/tour";
import { useSnackbar } from "notistack";
import { styled as muiStyled } from "@mui/material/styles";

import { Grid, Stack, Tooltip, Typography } from "@mui/material";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HouseIcon from "@mui/icons-material/House";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";

import { findAllSuggestArea, generateContent } from "src/api/generateAi";

import font from "src/styles/font";
import {
  Black,
  Grey,
  GreyStroke,
  Primary,
  White,
  White01,
} from "src/styles/color";
import { checkNotEmpty } from "src/common";
import { base64ToFile, compressImage, convertToBase64 } from "src/common/file";
import { useAuth } from "src/hooks/useAuth";
import { useLoading } from "src/context/LoadingContext";
import { useDialog } from "src/context/DialogContext";

import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import LabelSection from "src/components/core/LabelSection";
import UploadImage from "src/components/upload/dropzone/UploadImage";

import ImageGalleryDialog from "src/modules/product/upload/modal/ImageGalleryDialog";

const Dialog = muiStyled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "580px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
      position: "relative",
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 8px;
    right: 8px;
    color: ${Black};
  }
`;

const AdBoxTitle = styled.div`
  width: 100%;
  /* height: max-content; */
  display: flex;
`;

const TokenBalanceWarning = styled.div`
  margin: 12px auto;
  padding: 8px 16px;
  width: max-content;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: #ca3500;
  background-color: #fff7ed;
  border: 1px solid #ffedd4;

  .MuiSvgIcon-root {
    color: #ff6900;
  }
  .MuiTypography-root {
    color: #ca3500;
  }
`;

const AreaCard = styled.div`
  padding: 8px;
  border: 1px solid ${GreyStroke};
  border-radius: 12px;
  cursor: pointer;
  text-align: center;

  > p {
    margin: 0;
    font-size: ${font.size.xs};
    font-weight: 400;
  }

  ${({ $active }) =>
    $active &&
    css`
      border: 1px solid transparent;
      outline: 2px solid #9ad3f0;
      background-color: #e1f5fe;
    `}
`;

const AreaCardImage = styled.img`
  width: 70px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  background-color: ${Grey};
`;

const WrapperTextField = styled.div`
  // margin-bottom: 16px;
  width: 100%;

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
    // text-align: center;
    // background: #fff;
  }
  .TextField-without-border-radius-amount fieldset {
    border-radius: 16px;
    width: 100%;
    text-align: center;
    background: #fff;
  }
  .MuiOutlinedInput-input {
    background: #fff;

    border-radius: 16px;
  }
  .MuiInputBase-root-MuiOutlinedInput-root {
    border-radius: 16px;
    background: #fff;
  }
  .MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root {
    border-radius: 16px;
  }
`;

const UploaderContainer = styled.div`
  width: 100%;
  padding-top: 100%;
  position: relative;
`;

const WrapperUploadImage = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${White01};
  /* border: 2px dashed ${GreyStroke}; */
  border-radius: 24px;
  overflow: hidden;

  & > section > div,
  & > section > div > div {
    width: 100%;
    height: 100%;
  }
`;

const UploadHover = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: #f3f1f290;
  border-radius: inherit;
  display: none;
  top: 0;
  align-items: center;
  justify-content: center;
  visibility: hidden;
`;

const WrapperImage = styled.div`
  width: 100%;
  height: 100%;
  /* padding: 10px; */
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 24px;

  &:hover {
    .hover-upload {
      display: flex;
      visibility: visible;
    }
  }
`;

const WrapperEmptyImage = styled(WrapperImage)`
  border: 2px dashed ${GreyStroke};
`;

const WrapperResultImage = styled.div`
  width: 100%;
  max-width: 520px;
  height: auto;
  border-radius: 16px;
`;

const ImgShow = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

const EmptyText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${Grey};
  text-align: center;
  margin-top: 8px;
`;

/* ================= TOUR CONFIG ================= */

const TOUR_KEY = "generate-image-tour-done";

const tourSteps = [
  {
    selector: ".tour-select-area",
    content: "เลือกพื้นที่การใช้งานของวัสดุของคุณที่นี่",
  },
  {
    selector: ".tour-prompt-input",
    content: "ปรับ Prompt เพื่อให้ได้ภาพตรงตามที่ต้องการ",
  },
  { selector: ".tour-room-image", content: "อัปโหลดหรือเลือกภาพมุมห้องต้นแบบ" },
  { selector: ".tour-material-image", content: "อัปโหลดภาพวัสดุที่ต้องการใช้" },
  {
    selector: ".tour-generate-button",
    content: "เมื่อกรอกครบแล้ว กดสร้างภาพได้เลย",
  },
];

const TourRunner = ({ run }) => {
  const { setIsOpen } = useTour();

  useEffect(() => {
    if (run) setIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  return null;
};

const StepButton = ({ variant = "text", ...props }) => {
  return (
    <Button
      variant={variant}
      size="small"
      sx={{
        "&.MuiButton-root": {
          padding: "4px 16px",
          borderRadius: "8px",
        },
      }}
      {...props}
    >
      {props.children}
    </Button>
  );
};

/* ================= END TOUR CONFIG ================= */

const GenerateImageDialog = ({
  itemCode = null,
  isOpen,
  onSuccess,
  onClose,
}) => {
  const { user, userTokenBalance, checkGenerateTokenBalance } = useAuth();
  const { openDialog, closeDialog } = useDialog();
  const { isLoading, setLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();

  const uploadRoomRef = useRef(null);
  const uploadMatRef = useRef(null);

  // Guide Tour
  const [runTour, setRunTour] = useState(false);

  const [suggestAreas, setSuggestAreas] = useState([]);
  const [suggestAreaGallery, setSuggestAreaGallery] = useState([]);
  const defaultSelectedObject = {
    area: "",
    prompt: "",
    roomImage: null,
    materialImage: null,
  };
  const [selectedObject, setSelectedObject] = useState(defaultSelectedObject);
  const [resultContent, setResultContent] = useState(null);
  const [isReady, setIsReady] = useState(false); // check data ready before start tour

  const isFormValid =
    ["prompt", "roomImage", "materialImage"].every((required) => {
      let objVal = selectedObject[required];
      return checkNotEmpty(objVal);
    }) && (user?.role_id === 3 ? userTokenBalance?.current_balance > 6 : true);

  /**
   * @note fetch suggest areas when open dialog
   */
  useEffect(() => {
    if (!isOpen) return;

    Promise.all([fetchSuggestAreas()]).then(() => setIsReady(true));
  }, [isOpen]);

  /**
   * @note check all data/component is ready before start tour
   */
  useEffect(() => {
    if (isReady && !localStorage.getItem(TOUR_KEY)) {
      setRunTour(true);
    }
  }, [isReady]);

  const fetchSuggestAreas = async () => {
    try {
      const data = await findAllSuggestArea();

      setSuggestAreas(data);
    } catch (err) {
      console.error("Error fetching suggest areas:", err);
    }
  };

  const handleSelectArea = (areaData) => {
    setSelectedObject({
      ...selectedObject,
      area: areaData.areaType,
      prompt: areaData.defaultPrompt,
    });
    setSuggestAreaGallery(areaData.suggestImages || []);
  };

  const handleOnSubmit = async () => {
    try {
      setLoading(true);
      let fileImage1 = selectedObject.roomImage;
      let fileImage2 = selectedObject.materialImage;

      // ตรวจสอบขนาดไฟล์และบีบอัดถ้าจำเป็น (เกิน 2MB)
      if (fileImage1.size > 2 * 1024 * 1024) {
        fileImage1 = await compressImage(selectedObject.roomImage, 800, 0.8);
      }
      if (fileImage2.size > 2 * 1024 * 1024) {
        fileImage2 = await compressImage(
          selectedObject.materialImage,
          800,
          0.8,
        );
      }

      // แปลงรูปภาพเป็น base64
      let image1Base64 = await convertToBase64(fileImage1);
      let image2Base64 = await convertToBase64(fileImage2);

      const payload = {
        prompt: selectedObject.prompt,
        model: "gemini-3-pro-image-preview",
        image1Base64: image1Base64.split(",")[1], // Remove data:image/jpeg;base64, part
        image1Type: selectedObject.roomImage.type, // image mime type
        image2Base64: image2Base64.split(",")[1], // Remove data:image/jpeg;base64, part
        image2Type: selectedObject.materialImage.type, // image mime type
        ...(!!itemCode && { itemCode: itemCode }), // pass itemCode if available for save in history
      };

      const responseData = await generateContent(payload);

      let resultContent = [];

      if (responseData?.parts && Array.isArray(responseData.parts)) {
        responseData.parts.forEach(async (part, i) => {
          if (part.type === "image") {
            // แปลง base64 เป็นไฟล์
            const file = await base64ToFile(
              part.data,
              `generated-image-${i}.${part.mimeType.split("/")[1]}`,
              part.mimeType,
            );

            resultContent.push(file);
          } else {
            console.warn("Unsupported part type:", part.type);
          }
        });

        setResultContent(resultContent);
        checkGenerateTokenBalance();
      }
    } catch (error) {
      console.error("Error generating images:", error);
      if (
        error.response &&
        error.response?.data?.message &&
        error.response?.data?.message.includes("The model is overloaded.")
      ) {
        enqueueSnackbar(
          `เกิดข้อผิดพลาด กรุณารอสักครู่แล้วลองใหม่อีกครั้งในภายหลัง`,
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(
          "เกิดข้อผิดพลาดในการสร้างภาพห้อง กรุณาลองใหม่อีกครั้ง",
          {
            variant: "error",
          },
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOnClose = () => {
    openDialog({
      type: "confirm",
      maxWidth: 350,
      title: "คุณแน่ใจหรือไม่ที่จะปิดหน้าต่างนี้?",
      onConfirm: () => {
        onClose();
      },
    });
  };

  return (
    <>
      <Dialog
        onClose={handleOnClose}
        aria-labelledby="customized-dialog-title"
        open={true}
      >
        <TourProvider
          steps={tourSteps}
          showNavigation
          showBadge={false}
          showDots={false}
          disableDotsNavigation
          styles={{
            maskArea: (base) => ({
              ...base,
              rx: 14,
            }),
            popover: (base) => ({
              ...base,
              zIndex: 100000,
              padding: "16px 24px",
              borderRadius: "8px",
              width: "420px",
              fontSize: "1rem",
              textAlign: "center",
            }),
            badge: (base) => ({ ...base, background: Primary }),
          }}
          components={{
            Close: ({ onClick }) => (
              <CloseButton
                sx={{
                  "&.MuiIconButton-root": {
                    top: 0,
                    right: 0,
                  },
                  ".MuiSvgIcon-root": {
                    fontSize: 16,
                  },
                }}
                aria-label="close"
                onClick={onClick}
              >
                <CloseIcon fontSize="small" />
              </CloseButton>
            ),
          }}
          prevButton={({ currentStep, setCurrentStep }) => (
            <StepButton
              variant="text"
              onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)}
              disabled={currentStep === 0}
            >
              Back
            </StepButton>
          )}
          nextButton={({ currentStep, steps, setCurrentStep, setIsOpen }) => (
            <StepButton
              variant="contained"
              onClick={() =>
                currentStep < steps.length - 1
                  ? setCurrentStep(currentStep + 1)
                  : setIsOpen(false)
              }
            >
              {currentStep < steps.length - 1
                ? `Next (Step ${currentStep + 1} of ${steps.length})`
                : "Finish"}
            </StepButton>
          )}
          afterOpen={(target) => {
            if (!target) return;

            target.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }}
          beforeClose={() => localStorage.setItem(TOUR_KEY, "true")}
        >
          <TourRunner run={runTour} />

          <DialogTitle
            sx={{
              p: 0,
              // textAlign: "center",
            }}
          >
            <AdBoxTitle>
              <img
                src="/assets/banner-generate-02.jpg"
                alt="ad"
                width="100%"
                height="100%"
              />
            </AdBoxTitle>

            {user?.role_id === 3 &&
              userTokenBalance?.current_balance <= 6 &&
              !checkNotEmpty(resultContent) && (
                <TokenBalanceWarning>
                  <ErrorOutlineIcon />
                  <Typography variant="body2" ml={1} fontSize={font.size.sm}>
                    คุณมีจำนวนโทเค็นไม่เพียงพอต่อการใช้งาน
                    กรุณาเติมโทเค็นเพื่อใช้งานฟีเจอร์นี้
                  </Typography>
                </TokenBalanceWarning>
              )}

            <CloseButton aria-label="close" onClick={handleOnClose}>
              <CloseIcon fontSize="small" sx={{ color: White }} />
            </CloseButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: 3,
              width: "100%",
              "&.MuiDialogContent-root": {
                pt: 2,
              },
              "::-webkit-scrollbar": {
                display: "none",
              },
              "& .MuiBox-root": {
                maxWidth: "530px",
                mx: "auto",
              },
            }}
          >
            {checkNotEmpty(resultContent) ? (
              <LabelSection
                title="ภาพผลลัพธ์ที่ได้"
                style={{ marginBottom: 0 }}
                headerStyle={{ marginBottom: 12 }}
              >
                <Stack
                  columnGap={1}
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="center"
                >
                  {resultContent.map((file, index) => (
                    <WrapperResultImage key={`generated-image-${index}`}>
                      <ImgShow src={file.fileData} alt={`selected-image`} />
                    </WrapperResultImage>
                  ))}
                </Stack>
              </LabelSection>
            ) : (
              <>
                <LabelSection
                  title="เลือกพื้นที่ใช้งานของวัสดุ"
                  style={{ marginBottom: 16 }}
                  headerStyle={{ marginBottom: 16 }}
                >
                  <Stack
                    className="tour-select-area"
                    flexDirection="row"
                    gap={2}
                    justifyContent="center"
                    alignItems="center"
                    mb={2}
                  >
                    {suggestAreas.map((area, index) => (
                      <AreaCard
                        key={index}
                        $active={selectedObject.area === area.areaType}
                        onClick={() => {
                          handleSelectArea(area);
                        }}
                      >
                        <AreaCardImage src={area?.icon?.docUrl} />
                        <p>{area.areaLabel.areaTh}</p>
                      </AreaCard>
                    ))}
                  </Stack>

                  <WrapperTextField className="tour-prompt-input">
                    <TextField
                      label="Prompt สำหรับสร้างภาพห้อง"
                      variant="outlined"
                      className="TextField-without-border-radius"
                      multiline
                      minRows={3}
                      value={selectedObject.prompt}
                      onChange={(e) =>
                        setSelectedObject({
                          ...selectedObject,
                          prompt: e.target.value,
                        })
                      }
                      fullWidth
                    />
                  </WrapperTextField>
                </LabelSection>

                <LabelSection
                  title="อัพโหลดภาพมุมห้องและภาพวัสดุที่ต้องการ"
                  style={{ marginBottom: 0 }}
                  headerStyle={{ marginBottom: 16 }}
                >
                  <Grid
                    container
                    columnSpacing={4}
                    rowSpacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Grid item xs={6}>
                      <UploaderContainer className="tour-room-image">
                        <WrapperUploadImage>
                          <UploadImage
                            ref={uploadRoomRef}
                            callBackFile={(fileCallBlack) => {
                              fileCallBlack !== "" &&
                                setSelectedObject({
                                  ...selectedObject,
                                  roomImage: fileCallBlack,
                                });
                              if (resultContent) {
                                setResultContent(null);
                              }
                            }}
                            style={{ height: "100%" }}
                            closeAlert={false}
                          >
                            {checkNotEmpty(
                              selectedObject?.roomImage?.fileData,
                            ) ? (
                              <WrapperImage>
                                <ImgShow
                                  src={selectedObject?.roomImage?.fileData}
                                  alt={`selected-image`}
                                />

                                <UploadHover className="hover-upload">
                                  <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                      width: "max-content",
                                      borderRadius: 4,
                                    }}
                                  >
                                    Change
                                  </Button>
                                </UploadHover>
                              </WrapperImage>
                            ) : (
                              <WrapperEmptyImage>
                                <AddPhotoAlternateOutlinedIcon
                                  sx={{
                                    fontSize: 48,
                                    color: `${Grey}`,
                                  }}
                                />
                                <EmptyText>อัปโหลด/เลือกภาพห้อง</EmptyText>
                              </WrapperEmptyImage>
                            )}
                          </UploadImage>
                        </WrapperUploadImage>
                      </UploaderContainer>

                      <Stack
                        columnGap={1}
                        flexDirection="row"
                        alignItems="center"
                        mt={2}
                      >
                        <Button
                          variant="outlined"
                          sx={{
                            p: "8px 12px",
                            borderRadius: 3,
                            minWidth: "48%",
                          }}
                          onClick={() => uploadRoomRef.current?.open()}
                        >
                          อัปโหลดภาพห้อง
                        </Button>

                        <Tooltip
                          title={
                            suggestAreaGallery.length === 0
                              ? "กรุณาเลือกพื้นที่ใช้งานของวัสดุก่อน"
                              : ""
                          }
                          TransitionProps={{ timeout: 300 }}
                          placement="top"
                        >
                          <Button
                            variant="outlined"
                            sx={{
                              p: "8px 12px",
                              borderRadius: 3,
                              minWidth: "48%",
                            }}
                            disabled={suggestAreaGallery.length === 0}
                            onClick={() =>
                              openDialog({
                                type: "custom",
                                customDialog: (
                                  <ImageGalleryDialog
                                    suggestImages={suggestAreaGallery}
                                    onSubmit={(fileCallBlack) => {
                                      fileCallBlack !== "" &&
                                        setSelectedObject({
                                          ...selectedObject,
                                          roomImage: fileCallBlack,
                                        });
                                      closeDialog();
                                    }}
                                    onClose={() => closeDialog()}
                                  />
                                ),
                              })
                            }
                          >
                            เลือกภาพห้อง
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Grid>

                    <Grid item xs={6}>
                      <UploaderContainer className="tour-material-image">
                        <WrapperUploadImage>
                          <UploadImage
                            ref={uploadMatRef}
                            callBackFile={(fileCallBlack) => {
                              fileCallBlack !== "" &&
                                setSelectedObject({
                                  ...selectedObject,
                                  materialImage: fileCallBlack,
                                });
                              if (resultContent) {
                                setResultContent(null);
                              }
                            }}
                            style={{ height: "100%" }}
                            closeAlert={false}
                          >
                            {checkNotEmpty(
                              selectedObject?.materialImage?.fileData,
                            ) ? (
                              <WrapperImage>
                                <ImgShow
                                  src={selectedObject?.materialImage?.fileData}
                                  alt={`selected-image`}
                                />

                                <UploadHover className="hover-upload">
                                  <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                      width: "max-content",
                                      borderRadius: 4,
                                    }}
                                  >
                                    Change
                                  </Button>
                                </UploadHover>
                              </WrapperImage>
                            ) : (
                              <WrapperEmptyImage>
                                <AddPhotoAlternateOutlinedIcon
                                  sx={{
                                    fontSize: 48,
                                    color: `${Grey}`,
                                  }}
                                />
                                <EmptyText>อัปโหลดภาพวัสดุ</EmptyText>
                              </WrapperEmptyImage>
                            )}
                          </UploadImage>
                        </WrapperUploadImage>
                      </UploaderContainer>

                      <Stack
                        columnGap={1}
                        flexDirection="row"
                        alignItems="center"
                        mt={2}
                      >
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ p: "8px 12px", borderRadius: 3 }}
                          onClick={() => uploadMatRef.current?.open()}
                        >
                          อัปโหลดภาพวัสดุ
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </LabelSection>
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pt: 2, pb: 3 }}>
            {checkNotEmpty(resultContent) ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => onSuccess(resultContent[0])}
                  sx={{ minWidth: 140 }}
                >
                  ใช้ภาพนี้
                </Button>
                <Button
                  variant="outlined"
                  // color="secondary"
                  size="large"
                  onClick={() => {
                    openDialog({
                      type: "confirm",
                      maxWidth: 350,
                      title:
                        "หากต้องการสร้างภาพใหม่จะต้องใช้โทเค็นในการสร้างอีกครั้ง คุณแน่ใจหรือไม่ที่จะสร้างภาพใหม่?",
                      onConfirm: () => {
                        setResultContent(null);
                      },
                    });
                  }}
                  sx={{ minWidth: 140 }}
                >
                  สร้างภาพใหม่
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="large"
                fullWidth
                className="tour-generate-button"
                startIcon={<HouseIcon />}
                onClick={() => handleOnSubmit()}
                disabled={!isFormValid || isLoading}
                sx={{ maxWidth: 220 }}
              >
                สร้างภาพห้องด้วย AI
              </Button>
            )}
          </DialogActions>
        </TourProvider>
      </Dialog>
    </>
  );
};

export default GenerateImageDialog;
